"use client";

import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

interface ContactSectionProps {
  showToast: (msg: string) => void;
}

export default function ContactSection({ showToast }: ContactSectionProps) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterPhone, setNewsletterPhone] = useState("");
  const [emailToggle, setEmailToggle] = useState(true);
  const [smsToggle, setSmsToggle] = useState(true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormData({ name: "", email: "", company: "", message: "" });
    showToast(t.contactToast);
  };

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setNewsletterEmail("");
    setNewsletterPhone("");
    showToast(t.newsletterToast);
  };

  return (
    <>
      {/* Contact Section */}
      <section
        id="kontakt"
        style={{ backgroundColor: "#0A0A0A", borderTop: "1px solid #2A2A2A", padding: "120px 0" }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "80px", alignItems: "start" }}>
            {/* Left Info */}
            <div>
              <p className="section-label" style={{ marginBottom: "16px" }}>{t.contactLabel}</p>
              <h2
                style={{
                  fontFamily: "Rajdhani, sans-serif",
                  fontWeight: 700,
                  fontSize: "clamp(2.5rem, 4vw, 3.5rem)",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: "white",
                  lineHeight: 1.05,
                  marginBottom: "32px",
                }}
              >
                {t.contactHeading}
              </h2>
              <p
                style={{
                  fontFamily: "Nunito Sans, sans-serif",
                  fontSize: "1rem",
                  color: "rgba(255,255,255,0.5)",
                  lineHeight: 1.8,
                  marginBottom: "48px",
                  maxWidth: "400px",
                }}
              >
                {t.contactBody}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                {[
                  { label: t.contactEmailLabel, value: "kontakt@sebnorge.com", href: "mailto:kontakt@sebnorge.com" },
                  { label: t.contactPhoneLabel, value: "+47 22 83 83 10", href: "tel:+4722838310" },
                  { label: t.contactAddressLabel, value: "Karl Johans gate 1, 0154 Oslo", href: undefined },
                  { label: t.contactHoursLabel, value: t.contactHoursValue, href: undefined },
                ].map((item) => (
                  <div key={item.label} style={{ borderBottom: "1px solid #1A1A1A", paddingBottom: "20px" }}>
                    <p className="section-label" style={{ marginBottom: "6px" }}>{item.label}</p>
                    {item.href ? (
                      <a href={item.href} style={{ fontFamily: "Nunito Sans, sans-serif", fontSize: "0.95rem", color: "rgba(255,255,255,0.7)", textDecoration: "none" }}
                        onMouseEnter={e => (e.currentTarget.style.color = 'white')}
                        onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.7)')}>
                        {item.value}
                      </a>
                    ) : (
                      <p style={{ fontFamily: "Nunito Sans, sans-serif", fontSize: "0.95rem", color: "rgba(255,255,255,0.7)" }}>
                        {item.value}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Right Form */}
            <div>
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "28px" }}>
                <div>
                  <label className="section-label" style={{ display: "block", marginBottom: "8px" }}>{t.contactFormName}</label>
                  <input
                    type="text"
                    className="input-line"
                    placeholder={t.contactFormNamePlaceholder}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="section-label" style={{ display: "block", marginBottom: "8px" }}>{t.contactFormEmail}</label>
                  <input
                    type="email"
                    className="input-line"
                    placeholder={t.contactFormEmailPlaceholder}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="section-label" style={{ display: "block", marginBottom: "8px" }}>{t.contactFormCompany}</label>
                  <input
                    type="text"
                    className="input-line"
                    placeholder={t.contactFormCompanyPlaceholder}
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  />
                </div>
                <div>
                  <label className="section-label" style={{ display: "block", marginBottom: "8px" }}>{t.contactFormMessage}</label>
                  <textarea
                    className="input-line"
                    placeholder={t.contactFormMessagePlaceholder}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={4}
                    style={{ resize: "vertical" }}
                    required
                  />
                </div>
                <button type="submit" className="btn-full-white">
                  {t.contactFormSubmit}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Opt-in Strip */}
      <section
        style={{
          backgroundColor: "#111111",
          borderTop: "1px solid #2A2A2A",
          padding: "80px 0",
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "80px", alignItems: "center" }}>
            {/* Left */}
            <div>
              <p className="section-label" style={{ marginBottom: "12px" }}>{t.newsletterLabel}</p>
              <h3
                style={{
                  fontFamily: "Rajdhani, sans-serif",
                  fontWeight: 700,
                  fontSize: "clamp(1.5rem, 3vw, 2.2rem)",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "white",
                  lineHeight: 1.1,
                  marginBottom: "16px",
                }}
              >
                {t.newsletterHeading}
              </h3>
              <p
                style={{
                  fontFamily: "Nunito Sans, sans-serif",
                  fontSize: "0.9rem",
                  color: "rgba(255,255,255,0.45)",
                  lineHeight: 1.7,
                  maxWidth: "420px",
                }}
              >
                {t.newsletterBody}
              </p>
            </div>

            {/* Right Form */}
            <form onSubmit={handleNewsletterSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                <div>
                  <label className="section-label" style={{ display: "block", marginBottom: "6px" }}>{t.newsletterEmailLabel}</label>
                  <input
                    type="email"
                    className="input-line"
                    placeholder={t.newsletterEmailPlaceholder}
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="section-label" style={{ display: "block", marginBottom: "6px" }}>{t.newsletterPhoneLabel}</label>
                  <input
                    type="tel"
                    className="input-line"
                    placeholder={t.newsletterPhonePlaceholder}
                    value={newsletterPhone}
                    onChange={(e) => setNewsletterPhone(e.target.value)}
                  />
                </div>
              </div>
              <div style={{ display: "flex", gap: "24px", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <label className="toggle-switch">
                    <input type="checkbox" checked={emailToggle} onChange={(e) => setEmailToggle(e.target.checked)} />
                    <span className="toggle-slider" />
                  </label>
                  <span style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)" }}>{t.newsletterEmailToggle}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <label className="toggle-switch">
                    <input type="checkbox" checked={smsToggle} onChange={(e) => setSmsToggle(e.target.checked)} />
                    <span className="toggle-slider" />
                  </label>
                  <span style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)" }}>{t.newsletterSmsToggle}</span>
                </div>
              </div>
              <button type="submit" className="btn-full-white">
                {t.newsletterSubmit}
              </button>
              <p style={{ fontFamily: "Nunito Sans, sans-serif", fontSize: "0.7rem", color: "rgba(255,255,255,0.25)" }}>
                {t.newsletterDisclaimer}
              </p>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}